import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import clientService from '../../../services/clientService';
import clientRequirementsService from '../../../services/clientRequirementsService';

const AddClientModal = ({ isOpen, onClose, client, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [existingRequirements, setExistingRequirements] = useState([]);
  const [formData, setFormData] = useState({
    client_code: '',
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    industry: '',
    address: '',
    gst_number: '',
    status: 'active'
  });

  useEffect(() => {
    if (client) {
      setFormData({
        client_code: client.client_code || '',
        company_name: client.company_name || '',
        contact_person: client.contact_person || '',
        email: client.email || '',
        phone: client.phone || '',
        industry: client.industry || '',
        address: client.address || '',
        gst_number: client.gst_number || '',
        status: client.status || 'active'
      });

      // Load existing requirements for this client
      loadClientRequirements(client.id);
    } else {
      setFormData({
        client_code: '',
        company_name: '',
        contact_person: '',
        email: '',
        phone: '',
        industry: '',
        address: '',
        gst_number: '',
        status: 'active'
      });
      setRequirements([]);
      setExistingRequirements([]);
    }
    setError(null);
  }, [client, isOpen]);

  const loadClientRequirements = async (clientId) => {
    try {
      const result = await clientRequirementsService.getRequirements({ client_id: clientId });
      if (result?.success) {
        setExistingRequirements(result.data || []);
      }
    } catch (err) {
      console.error('Failed to load client requirements:', err);
    }
  };

  const addRequirement = () => {
    setRequirements([...requirements, {
      id: Date.now(), // Temporary ID
      position_title: '',
      quantity_needed: 1,
      department: '',
      experience_level: '',
      priority: 'medium',
      deadline: ''
    }]);
  };

  const removeRequirement = (index) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };

  const updateRequirement = (index, field, value) => {
    const updated = [...requirements];
    updated[index] = { ...updated[index], [field]: value };
    setRequirements(updated);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.company_name.trim()) {
      setError('Company name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, create or update the client
      const result = client
        ? await clientService.updateClient(client.id, formData)
        : await clientService.createClient(formData);

      if (!result?.success) {
        setError(result?.error || `Failed to ${client ? 'update' : 'create'} client`);
        return;
      }

      const clientId = result.data?.id || client?.id;

      // Then, create requirements if any
      if (requirements.length > 0 && clientId) {
        for (const req of requirements) {
          if (req.position_title.trim()) {
            const requirementData = {
              client_id: clientId,
              title: req.position_title,
              position_title: req.position_title,
              department: req.department || null,
              quantity_needed: parseInt(req.quantity_needed) || 1,
              experience_level: req.experience_level || null,
              priority: req.priority || 'medium',
              deadline: req.deadline || null,
              status: 'open'
            };

            await clientRequirementsService.createRequirement(requirementData);
          }
        }
      }

      onSave?.();
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Client save error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-background rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-foreground">
            {client ? 'Edit Client' : 'Add New Client'}
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client Code Field */}
          {client ? (
            <div className="md:col-span-2">
              <Input
                label="Client Code"
                value={formData.client_code || ''}
                disabled
                className="bg-muted cursor-not-allowed"
              />
            </div>
          ) : (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Client Code
              </label>
              <div className="px-3 py-2 border border-border rounded-md bg-muted text-muted-foreground flex items-center gap-2">
                <Icon name="Hash" size={16} />
                <span>Auto-generated on save (e.g., CLT-0001)</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Company Name *"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                required
                placeholder="Enter company name"
              />
            </div>

            <Input
              label="Contact Person"
              value={formData.contact_person}
              onChange={(e) => handleInputChange('contact_person', e.target.value)}
              placeholder="Contact person name"
            />

            <Input
              label="Industry"
              value={formData.industry}
              onChange={(e) => handleInputChange('industry', e.target.value)}
              placeholder="e.g., IT, Manufacturing"
            />

            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="company@example.com"
            />

            <Input
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="+91 1234567890"
            />

            <Input
              label="GST Number"
              value={formData.gst_number}
              onChange={(e) => handleInputChange('gst_number', e.target.value)}
              placeholder="29ABCDE1234F1Z5"
            />

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Address
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                rows="3"
                placeholder="Full address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Client Requirements Section */}
          <div className="md:col-span-2 pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold text-foreground">Client Requirements</h4>
                <p className="text-sm text-muted-foreground">Add position requirements with quantity needed</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRequirement}
                className="flex items-center gap-2"
              >
                <Icon name="Plus" size={16} />
                Add Requirement
              </Button>
            </div>

            {/* Existing Requirements (Read-only when editing) */}
            {client && existingRequirements.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-foreground mb-2">Existing Requirements:</p>
                <div className="space-y-2">
                  {existingRequirements.map((req) => (
                    <div key={req.id} className="p-3 bg-muted/50 rounded-lg border border-border">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-foreground">{req.position_title || req.title}</p>
                          <p className="text-sm text-muted-foreground">
                            Quantity: {req.quantity_needed || 1} •
                            {req.department && ` Department: ${req.department} •`}
                            Status: {req.status}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Requirements Form */}
            {requirements.length > 0 && (
              <div className="space-y-4">
                {requirements.map((req, index) => (
                  <div key={req.id} className="p-4 bg-muted/30 rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-medium text-foreground">Requirement #{index + 1}</h5>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRequirement(index)}
                        className="text-destructive"
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="md:col-span-2">
                        <Input
                          label="Position Title *"
                          value={req.position_title}
                          onChange={(e) => updateRequirement(index, 'position_title', e.target.value)}
                          placeholder="e.g., Software Developer, Data Analyst"
                          required
                        />
                      </div>
                      <Input
                        label="Required Nos (Quantity) *"
                        type="number"
                        min="1"
                        value={req.quantity_needed}
                        onChange={(e) => updateRequirement(index, 'quantity_needed', parseInt(e.target.value) || 1)}
                        required
                      />
                      <Input
                        label="Department"
                        value={req.department}
                        onChange={(e) => updateRequirement(index, 'department', e.target.value)}
                        placeholder="e.g., IT, HR, Finance"
                      />
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Experience Level
                        </label>
                        <select
                          value={req.experience_level}
                          onChange={(e) => updateRequirement(index, 'experience_level', e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                        >
                          <option value="">Select Level</option>
                          <option value="entry">Entry Level</option>
                          <option value="junior">Junior (1-3 years)</option>
                          <option value="intermediate">Intermediate (3-5 years)</option>
                          <option value="senior">Senior (5+ years)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Priority
                        </label>
                        <select
                          value={req.priority}
                          onChange={(e) => updateRequirement(index, 'priority', e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                      <Input
                        label="Deadline (Optional)"
                        type="date"
                        value={req.deadline}
                        onChange={(e) => updateRequirement(index, 'deadline', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {requirements.length === 0 && !client && (
              <div className="text-center py-6 border border-dashed border-border rounded-lg">
                <Icon name="Briefcase" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No requirements added yet</p>
                <p className="text-xs text-muted-foreground mt-1">Click "Add Requirement" to add position requirements</p>
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                  {client ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                client ? 'Update Client' : 'Create Client'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClientModal;

